














































































































import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash";
import { Location } from "vue-router";
import { BannerSubjectTypeEnum } from "@scrile/api-provider/dist/api/BannersProvider";
import { Page } from "@scrile/api-provider/dist/api/PageProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getProfileUrl } from "@/lib/urlHelpers";
import { EVENTS } from "@/const";
import BaseIcon from "@/components/BaseIcon";
import BasePage from "@/components/BasePage";
import InfiniteScroll from "@/components/InfiniteScroll";
import { MoreMenuItem } from "@/views/PageMore/config";
import LandingMultiselect from "./components/LandingMultiselect";
import LandingSlider from "./components/LandingSlider";
import ModelsList from "./components/ModelsList";

export default defineComponent({
  name: "PageLanding",
  components: { LandingSlider, BasePage, BaseIcon, ModelsList, InfiniteScroll, LandingMultiselect },
  props: {
    category: { type: String, default: null },
    screenName: { type: String, default: "" },
  },
  setup(props, { root }) {
    const bannerSubjectTypeEnum = BannerSubjectTypeEnum;
    const loading = ref<boolean>(true);
    const categories = ref<TagInfo[]>([]);
    const pages = ref<Page[]>([]);
    const { authUser } = useAuthUser();
    const followedIds = ref<string[]>([]);
    const allCreators = ref<number>(0);
    const availableCreators = ref<number>(0);
    const currentCategoryValue = computed<TagInfo | undefined>(() =>
      categories.value.find(i => i.slug === props.category)
    );
    const { enableScroll, items, getNextPage, getFirstPage } = usePager(page =>
      providers.PerformerListProvider.performerListSearch({
        data: {
          page,
          size: 9,
          sort: "IS_LIVE,DESC;FOLLOWER_COUNT,DESC",
          hideFromHomePage: false,
          name: props.screenName,
          tags: !!currentCategoryValue.value ? [currentCategoryValue.value.id] : undefined,
        },
      }).then(async performerListSearchResult => {
        if (authUser.value) {
          const ids = performerListSearchResult.result.map(user => user.id);
          if (ids.length) {
            const r = await providers.FollowingsProvider.searchFollowings({ ids });
            followedIds.value = [...followedIds.value, ...r.map(item => item.userId)];
          }
        }
        availableCreators.value = currentCategoryValue.value
          ? performerListSearchResult.totalRecords ?? 0
          : allCreators.value;
        return performerListSearchResult;
      })
    );
    const footerLinks = computed(() => {
      return pages.value.reduce((out, page, index) => {
        if (page.visible) out.push({ label: page.title, href: page.path, name: `term${index}` });
        return out;
      }, [] as MoreMenuItem[]);
    });

    const userProfileLocation = computed<Location>(() => getProfileUrl(authUser.value ?? ""));

    const search = debounce(() => {
      followedIds.value = [];
      loading.value = true;
      getFirstPage().finally(() => (loading.value = false));
    }, 500);

    const onInputSearch = async (value: string) => {
      root.$router.push({
        name: "index",
        query: {
          screenName: !!value ? value : undefined,
          category: !!currentCategoryValue.value ? currentCategoryValue.value?.slug : undefined,
        },
      });
      await search();
    };
    const onInputCategory = (e: TagInfo | null) => {
      if (currentCategoryValue.value === e) return;
      root.$router.push({
        name: "index",
        query: { screenName: !!props.screenName ? props.screenName : undefined, category: e?.slug },
      });
    };

    watch(authUser, async (curentUser, oldUser) => {
      if (curentUser && !oldUser) {
        const ids = items.value.map(user => user.id);
        const r = await providers.FollowingsProvider.searchFollowings({ ids });
        followedIds.value = [...followedIds.value, ...r.map(item => item.userId)];
      }
    });

    watch(currentCategoryValue, search);

    onMounted(() => {
      Promise.all([
        search(),
        providers.PageProvider.find().then(r => (pages.value = r)),
        providers.TagsProvider.findAll().then(
          r =>
            (categories.value = r.sort((a, b) => {
              if (a.title < b.title) return -1;
              if (a.title > b.title) return 1;
              return 0;
            }))
        ),
        providers.RolesProvider.getStats().then(r => (allCreators.value = r.info.creator ?? 0)),
      ]);
      emitter.emit(EVENTS.MAIN_MENU_HIDE, { fullHide: true });
    });

    onBeforeUnmount(() => {
      emitter.emit(EVENTS.MAIN_MENU_HIDE, { fullHide: false });
    });

    return {
      footerLinks,
      enableScroll,
      items,
      getNextPage,
      onInputSearch,
      loading,
      onInputCategory,
      categories,
      currentCategoryValue,
      userProfileLocation,
      authUser,
      availableCreators,
      followedIds,
      bannerSubjectTypeEnum,
    };
  },
});
